import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../shared/navbar';
import { API_BASE_URL } from '../../config';
import InvoicesPaymentsScreen from '../financial/InvoicesPaymentsScreen';
import ExpensesVendorsScreen from '../financial/ExpensesVendorsScreen';
import '../financial/PortfolioDashboard.css';
import './PropertyDetailView.css';


const PropertyDetailView = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unitPage, setUnitPage] = useState(1);
  const unitsPerPage = 30;

  useEffect(() => {
    fetchPropertyData();
  }, [propertyId, dateRange]);

  const fetchPropertyData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('start_date', dateRange.start_date);
      params.append('end_date', dateRange.end_date);

      const response = await fetch(`${API_BASE_URL}/api/portfolio/property/${propertyId}?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPropertyData(data);
      } else {
        setError('Failed to load property data');
      }
    } catch (err) {
      console.error('Error fetching property:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="property-detail-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading property data...</p>
        </div>
      </div>
    );
  }

  if (error || !propertyData) {
    return (
      <div className="property-detail-view">
        <div className="error-banner">{error || 'Property not found'}</div>
        <button onClick={() => navigate('/financial')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="property-detail-view">
      <Navbar selected="Financial" />
      <div className="detail-container">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <button onClick={() => navigate('/financial')} className="back-button">
            ← Back to Portfolio
          </button>
          <h1>{propertyData.property.property_name || propertyData.property.name}</h1>
          <p className="property-address">{propertyData.property.address}</p>
          <div className="property-stats-row">
            <div className="stat-item">
              <span className="stat-label">Units:</span>
              <span className="stat-value">{propertyData.stats.unit_count}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Occupancy:</span>
              <span className="stat-value">{formatPercent(propertyData.stats.occupancy_rate)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Avg Rent:</span>
              <span className="stat-value">{formatCurrency(propertyData.stats.average_rent)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">YTD NOI:</span>
              <span className="stat-value noi-value" style={{ color: propertyData.ytd_noi >= 0 ? '#10b981' : '#ef4444' }}>
                {formatCurrency(propertyData.ytd_noi)}
              </span>
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="date-range-selector">
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
              className="date-input"
            />
            <span>to</span>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
              className="date-input"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'units' ? 'active' : ''}
          onClick={() => setActiveTab('units')}
        >
          Units & Tenants
        </button>
        <button
          className={activeTab === 'invoices' ? 'active' : ''}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices & Payments
        </button>
        <button
          className={activeTab === 'expenses' ? 'active' : ''}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="detail-content">
          {/* Current Period KPIs */}
          <div className="kpi-grid-detail">
            <div className="kpi-card-detail">
              <div className="kpi-label-detail">Rent Due</div>
              <div className="kpi-value-detail">{formatCurrency(propertyData.current_period.rent_due)}</div>
              <div className="kpi-subtitle-detail">This period</div>
            </div>
            <div className="kpi-card-detail success">
              <div className="kpi-label-detail">Rent Collected</div>
              <div className="kpi-value-detail">{formatCurrency(propertyData.current_period.rent_collected)}</div>
              <div className="kpi-subtitle-detail">This period</div>
            </div>
            <div className="kpi-card-detail warning">
              <div className="kpi-label-detail">Expenses</div>
              <div className="kpi-value-detail">{formatCurrency(propertyData.current_period.expenses)}</div>
              <div className="kpi-subtitle-detail">This period</div>
            </div>
            <div className="kpi-card-detail noi">
              <div className="kpi-label-detail">Net Operating Income</div>
              <div className="kpi-value-detail" style={{ color: propertyData.current_period.noi >= 0 ? '#10b981' : '#ef4444' }}>
                {formatCurrency(propertyData.current_period.noi)}
              </div>
              <div className="kpi-subtitle-detail">This period</div>
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="dashboard-section">
            <h2>Income & Expenses (Last 12 Months)</h2>
            <div className="trends-chart">
              {propertyData.monthly_trends.map((month, idx) => (
                <div key={idx} className="trend-bar-group">
                  <div className="trend-bar">
                    <div 
                      className="trend-bar-income" 
                      style={{ height: `${(month.income / Math.max(...propertyData.monthly_trends.map(m => m.income))) * 100}%` }}
                    ></div>
                    <div 
                      className="trend-bar-expense" 
                      style={{ height: `${(month.expenses / Math.max(...propertyData.monthly_trends.map(m => Math.max(m.income, m.expenses)))) * 100}%` }}
                    ></div>
                  </div>
                  <div className="trend-label">{new Date(month.month).toLocaleDateString('en-US', { month: 'short' })}</div>
                  <div className="trend-values">
                    <div style={{ fontSize: '11px', color: '#10b981' }}>${(month.income / 1000).toFixed(0)}k</div>
                    <div style={{ fontSize: '11px', color: '#ef4444' }}>${(month.expenses / 1000).toFixed(0)}k</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expense Breakdown */}
          <div className="dashboard-section">
            <h2>Expense Breakdown</h2>
            {propertyData.expense_breakdown.length === 0 ? (
              <div className="empty-state">No expenses this period</div>
            ) : (
              <div className="expense-breakdown">
                {propertyData.expense_breakdown.map((item, idx) => (
                  <div key={idx} className="expense-item">
                    <div className="expense-category">{item.category}</div>
                    <div className="expense-amount">{formatCurrency(item.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Units & Tenants Tab */}
      {activeTab === 'units' && (
        <div className="detail-content">
          <div className="dashboard-section">
            <h2>Units & Tenants</h2>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Unit Number</th>
                    <th>Status</th>
                    <th>Tenant</th>
                    <th>Monthly Rent</th>
                    <th>Delinquency</th>
                  </tr>
                </thead>
                <tbody>
                  {propertyData.units.slice((unitPage - 1) * unitsPerPage, unitPage * unitsPerPage).map(unit => (
                    <tr 
                      key={unit.id}
                      onClick={() => unit.tenant_id && navigate(`/portfolio/tenant/${unit.tenant_id}`)}
                      className={unit.tenant_id ? "clickable-row" : ""}
                    >
                      <td>{unit.unit_number}</td>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: unit.status === 'occupied' ? '#d4edda' : 
                                         unit.status === 'maintenance' ? '#fff3cd' : '#e2e8f0',
                          color: unit.status === 'occupied' ? '#155724' : 
                                unit.status === 'maintenance' ? '#856404' : '#495057'
                        }}>
                          {unit.status}
                        </span>
                      </td>
                      <td>{unit.tenant_name || '-'}</td>
                      <td>{formatCurrency(unit.monthly_rent)}</td>
                      <td>
                        {unit.is_delinquent ? (
                          <span style={{ color: '#ef4444', fontWeight: 600 }}>Overdue</span>
                        ) : (
                          <span style={{ color: '#10b981' }}>Current</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {propertyData.units.length > unitsPerPage && (
              <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px' }}>
                <button 
                  onClick={() => setUnitPage(prev => Math.max(1, prev - 1))}
                  disabled={unitPage === 1}
                  className="btn-secondary"
                  style={{ padding: '5px 15px' }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '14px', color: '#64748b' }}>
                  Page {unitPage} of {Math.ceil(propertyData.units.length / unitsPerPage)}
                </span>
                <button 
                  onClick={() => setUnitPage(prev => Math.min(Math.ceil(propertyData.units.length / unitsPerPage), prev + 1))}
                  disabled={unitPage >= Math.ceil(propertyData.units.length / unitsPerPage)}
                  className="btn-secondary"
                  style={{ padding: '5px 15px' }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoices & Payments Tab */}
      {activeTab === 'invoices' && (
        <div className="detail-content detail-full-screen">
          <InvoicesPaymentsScreen 
            propertyId={propertyId} 
            dateRange={dateRange} 
          />
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="detail-content detail-full-screen">
          <ExpensesVendorsScreen 
            propertyId={propertyId} 
            dateRange={dateRange} 
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default PropertyDetailView;

